import { motion, AnimatePresence } from 'framer-motion';
import type { CommunicationMode } from '../types/app';

interface ReconnectionOverlayProps {
  isVisible: boolean;
  attempt: number;
  maxAttempts: number;
  onRetry: () => void;
  onSwitchMode?: () => void;
  suggestedMode?: CommunicationMode;
}

const modeLabels: Record<CommunicationMode, string> = {
  video: 'Video',
  audio: 'Audio',
  chat: 'Chat',
};

/**
 * Full-screen reconnection overlay with progress, countdown, and manual retry.
 *
 * During reconnection (attempt < maxAttempts):
 *   Semi-transparent dark overlay with centered spinner and attempt counter.
 *
 * After all attempts exhausted:
 *   Overlay stays, spinner replaced with error icon.
 *   "Connection Lost" heading with "Try Again" and optional "Switch to [mode]" buttons.
 */
export function ReconnectionOverlay({
  isVisible,
  attempt,
  maxAttempts,
  onRetry,
  onSwitchMode,
  suggestedMode,
}: ReconnectionOverlayProps) {
  const isExhausted = attempt >= maxAttempts;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          data-testid="reconnection-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl max-w-sm w-full mx-4 p-8 text-center"
          >
            {!isExhausted ? (
              <>
                {/* Spinner */}
                <div className="mb-6">
                  <svg
                    className="animate-spin h-12 w-12 text-brand-gold mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>

                <h2 className="text-xl font-serif text-brand-navy mb-2">
                  Reconnecting...
                </h2>
                <p className="text-gray-500 text-sm">
                  Attempt {attempt} of {maxAttempts}
                </p>
              </>
            ) : (
              <>
                {/* Error icon */}
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                    <svg
                      className="h-6 w-6 text-red-500"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </div>
                </div>

                <h2 className="text-xl font-serif text-brand-navy mb-2">
                  Connection Lost
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  We were unable to restore your connection.
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={onRetry}
                    className="w-full py-3 bg-brand-gold text-white font-medium rounded-lg hover:bg-brand-gold-dark transition-colors"
                  >
                    Try Again
                  </button>

                  {onSwitchMode && suggestedMode && (
                    <button
                      onClick={onSwitchMode}
                      className="w-full py-3 border border-brand-gold text-brand-gold font-medium rounded-lg hover:bg-brand-gold/5 transition-colors"
                    >
                      Switch to {modeLabels[suggestedMode]}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
